import { useEffect, useState } from "react";

import { Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { ButtonsGroup } from "shared/atoms/ButtonsGroup";
import { Placeholder } from "shared/atoms/Placeholder";
import { Video } from "shared/atoms/Video";

export type PreviewTab = "image" | "video";
interface Props {
  displayMediaTabs?: boolean;
  isLoading?: boolean;
  image_url: string;
  caption_file_url?: string;
  video_file_url?: string;
  video_thumbnail_file_url?: string;
  previewTab?: PreviewTab;
}

export const ImageAndVideoContainer: React.FC<Props> = ({
  displayMediaTabs = true,
  isLoading = false,
  image_url,
  caption_file_url,
  video_file_url,
  video_thumbnail_file_url,
  previewTab,
}) => {
  const { t } = useTranslation();
  const [selectedPreviewTab, setSelectedPreviewTab] = useState<PreviewTab>(
    previewTab || !video_file_url ? "image" : "video"
  );

  const hasBothMedia = image_url && video_file_url;

  useEffect(() => {
    if (previewTab) {
      setSelectedPreviewTab(previewTab);
    }
  }, [previewTab]);

  const getImage = (imageUrl?: string) => {
    if (!imageUrl) {
      return <Placeholder fontSize="120px" isLoading={isLoading} type="image" />;
    }
    return <img alt="exercise_image" src={imageUrl} />;
  };

  const getVideo = (videoFileUrl?: string, videoThumbnailFileUrl?: string) => {
    if (!videoFileUrl) {
      return <Placeholder fontSize="120px" isLoading={isLoading} type="video" />;
    }
    return <Video videoFileUrl={videoFileUrl} captionFileUrl={caption_file_url} poster={videoThumbnailFileUrl || ""} />;
  };

  return (
    <Stack spacing={2} alignItems="flex-start">
      <ContentContainer>
        {selectedPreviewTab === "image" ? getImage(image_url) : getVideo(video_file_url, video_thumbnail_file_url)}
      </ContentContainer>

      {displayMediaTabs && hasBothMedia && (
        <ButtonsGroup
          group={[
            {
              value: "image",
              label: t("common.image"),
            },
            {
              value: "video",
              label: t("common.video"),
            },
          ]}
          active={selectedPreviewTab}
          setActive={value => setSelectedPreviewTab(value as PreviewTab)}
        />
      )}
    </Stack>
  );
};

const ContentContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  border-radius: 8px;
  overflow: hidden;

  & > img {
    height: auto;
    max-width: 100%;
  }
`;
